<template>
  <div id="plan-card" class="pt-2 px-1">
    <div class="plan-card-top">
      <div :class="{ 'plan-card-top-min-height': !isMd }">
        <div
          v-if="showPlanTitle"
          class="plan-card-plan-title title-weight mb-1"
          :class="{ 'mb-3': plan === 'custom' || plan === 'noPlan' }"
        >
          <v-icon v-if="plan === 'premium'" class="plan-card-plan-title-icon-premium" size="22">
            mdi-rocket
          </v-icon>
          <v-icon v-if="plan === 'pro'" class="plan-card-plan-title-icon-premium" size="22">
            mdi-rocket-launch
          </v-icon>
          <v-icon v-if="plan === 'unlimited'" class="plan-card-plan-title-icon-premium" size="22">
            mdi-earth
          </v-icon>
          <!-- <v-icon v-else-if="plan === 'custom'" class="plan-card-plan-title-icon-custom" size="22">
            mdi-domain
          </v-icon> -->
          {{ $t(`views.common.upgradeModal.planCard.${plan}.title`) }}
        </div>

        <div class="py-2" :class="{ 'items-flex align-end': !isMd }">
          <div v-if="planPrice !== -1" class="plan-card-pricing">
            <div class="plan-card-pricing-price mt-3">
              <span v-if="plan === 'custom'" class="plan-card-pricing-from">
                {{ $t(`views.common.upgradeModal.planCard.from`) }}
              </span>
              <span :class="{ 'plan-card-pricing-price-line-through': planPromoPrice }">
                {{ planPrice }}
              </span>
              <span v-if="planPromoPrice">
                {{ planPromoPrice }}
              </span>

              <span class="plan-card-pricing-period">/{{ $t('common.month') }}</span>
            </div>
            <div
              v-if="['premium', 'pro', 'unlimited'].indexOf(plan) > -1 && !isMonthly"
              class="softTextGrey--text mt-2 plan-card-pricing-yearly-price"
            >
              <span>
                {{
                  planPromoCompleteYearlyPrice
                    ? planPromoCompleteYearlyPrice
                    : planCompleteYearlyPrice
                }}
              </span>
              {{ $t(`views.common.upgradeModal.planCard.yearlyInvoiced`) }}
            </div>
          </div>
        </div>
        <div v-if="planPrice === -1" class="plan-card-pricing-custom">
          {{ $t(`views.common.upgradeModal.planCard.customPrice`) }}
        </div>

        <v-chip v-if="planPromoPrice" class="my-1" color="softWarning" @click="$emit('go')">
          ¡Descuentos hasta el 6 agosto!
        </v-chip>
      </div>

      <v-divider class="mt-1 mb-4" :class="{ 'mt-4': isMd }" />

      <div class="plan-card-features pt-2">
        <div class="plan-card-features-feature items-flex align-start mb-2">
          <v-icon size="21" color="primary" class="plan-card-features-feature-icon mr-1">
            mdi-check-bold
          </v-icon>
          <div class="plan-card-features-feature-bold items-flex wrap">
            <template v-if="['unlimited', 'custom'].indexOf(plan) > -1">
              {{ $t(`views.common.upgradeModal.planCard.custom.totalCost`) }}
            </template>
            <template v-else>
              <currency :value="limitTotalCost" :currency="user.baseCurrency" neverHide />
              <span class="ml-1">
                {{ $t(`views.common.upgradeModal.planCard.${plan}.totalCost`) }}
              </span>
            </template>
          </div>
        </div>
        <div class="plan-card-features-feature items-flex align-start mb-4">
          <v-icon size="21" color="primary" class="plan-card-features-feature-icon mr-1">
            mdi-check-bold
          </v-icon>
          <div class="plan-card-features-feature-bold items-flex wrap">
            <template v-if="plan === 'custom'">
              {{ $t(`views.common.upgradeModal.planCard.custom.maxOperations`) }}
            </template>
            <template v-else>
              <currency :value="limitOperations" neverHide />
              <span class="ml-1 mr-1">
                {{ $t(`views.common.upgradeModal.planCard.${plan}.maxOperations`) }}
              </span>
              <template v-if="plan === 'unlimited'">
                <InfoIcon
                  :size="15"
                  :text="$t(`views.common.upgradeModal.planCard.unlimited.maxOperationsInfo`)"
                />
              </template>
            </template>
          </div>
        </div>
        <div
          v-for="(feature, idx) in features"
          :key="idx"
          class="plan-card-features-feature items-flex align-start mb-2"
        >
          <v-icon size="21" color="primary" class="plan-card-features-feature-icon mr-1">
            mdi-check-bold
          </v-icon>
          <span>
            {{ feature.text }}
            <sup v-if="feature.disclaimer">{{ feature.disclaimer }}</sup>
          </span>
        </div>
      </div>
    </div>

    <div class="plan-card-bottom">
      <v-divider class="my-2" />

      <div class="plan-card-go pt-3 pb-0" :class="{ 'px-0': !isMd }">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="disabled || loading || !allowedPlan"
          class="plan-card-go-button elevation-0"
          :class="{ 'big-button': !isMd }"
          :outlined="plan === 'custom'"
          @click="$emit('go')"
        >
          {{ $t(`views.common.upgradeModal.planCard.${plan}.go`) }}
        </v-btn>
        <template v-if="plan !== 'custom'">
          <div v-if="!allowedPlan" class="plan-card-go-disallowed-plan pt-2">
            {{ $t(`views.common.upgradeModal.planCard.disallowedPlan`) }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getCurrencyValueAsString } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency.vue'
import InfoIcon from '@/views/components/common/InfoIcon'

const PLAN_PRICES = {
  noPlan: {
    yearly: 0,
    completeYearly: 0,
    monthly: 0,
  },
  premium: {
    yearly: 9,
    // promoYearly: 4.5,
    completeYearly: 108,
    // promoCompleteYearly: 95.04,
    monthly: 11,
    // promoMonthly: 5.5,
  },
  pro: {
    yearly: 16,
    // promoYearly: 8,
    completeYearly: 192,
    // promoCompleteYearly: 168.96,
    monthly: 20,
    // promoMonthly: 10,
  },
  unlimited: {
    yearly: 20,
    completeYearly: 240,
    monthly: 24,
  },
  custom: {
    yearly: 20,
    completeYearly: null,
    monthly: 24,
  },
}

export default {
  components: { Currency, InfoIcon },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isMonthly: {
      type: Boolean,
      default: true,
    },
    plan: {
      type: String,
      required: true,
    },
    showPlanTitle: {
      type: Boolean,
      default: true,
    },
    allowedPlan: {
      type: Boolean,
      default: true,
    },
    limitOperations: {
      type: Number,
      default: 0,
    },
    limitTotalCost: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    ...mapState(['user']),
    isMd() {
      return this.$vuetify.breakpoint.smAndDown
    },
    features() {
      return Object.values(this.$t(`views.common.upgradeModal.planCard.${this.plan}.features`)).map(
        (text) => {
          return {
            text,
          }
        }
      )
    },
    planPromoPrice() {
      const value = this.isMonthly
        ? PLAN_PRICES[this.plan].promoMonthly
        : PLAN_PRICES[this.plan].promoYearly
      if (!value) return null
      return getCurrencyValueAsString({
        value,
        currency: 'EUR',
      })
    },
    planPrice() {
      const value = this.isMonthly ? PLAN_PRICES[this.plan].monthly : PLAN_PRICES[this.plan].yearly
      if (value === -1) return -1
      return getCurrencyValueAsString({
        value,
        currency: 'EUR',
      })
    },
    planPromoCompleteYearlyPrice() {
      const value = PLAN_PRICES[this.plan].promoCompleteYearly
      if (!value) return null
      return getCurrencyValueAsString({
        value,
        currency: 'EUR',
      })
    },
    planCompleteYearlyPrice() {
      const value = PLAN_PRICES[this.plan].completeYearly
      return getCurrencyValueAsString({
        value,
        currency: 'EUR',
      })
    },
  },
}
</script>

<style lang="scss">
#plan-card {
  text-align: left;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;

  .plan-card-top {
    .plan-card-top-min-height {
      min-height: 120px;
    }
  }

  .plan-card-bottom {
    padding-bottom: 10px;
  }

  .plan-card-plan-title {
    &.title-weight {
      font-weight: 300;
    }
    font-size: 22px;

    .plan-card-plan-title-icon-premium {
      margin-bottom: 4px;
    }
    .plan-card-plan-title-icon-custom {
      margin-bottom: 3px;
    }
  }
  .plan-card-plan-subtitle {
    font-weight: 500;
    font-size: 16px;
  }
  .items-flex {
    display: flex;
    &.align-start {
      align-items: flex-start;
    }
    &.align-end {
      align-items: flex-end;
    }
    &.justify-center {
      justify-content: center;
    }
    &.wrap {
      flex-wrap: wrap;
    }
  }
  .plan-card-pricing {
    .plan-card-pricing-price {
      font-size: 35px;
      font-weight: 500;
      .plan-card-pricing-from {
        font-size: 20px;
        font-weight: 400;
      }
      .plan-card-pricing-period {
        font-size: 16px;
      }
    }
    .plan-card-pricing-yearly-price {
      // font-style: italic;
      font-size: 13px;
    }

    .plan-card-pricing-price-line-through {
      text-decoration: line-through;
      font-weight: 200;
      text-decoration-thickness: 2px;
    }
  }
  .plan-card-pricing-custom {
    font-size: 25px;
    font-weight: 300;
  }
  .plan-card-promotion span {
    font-size: 11px;
  }

  .plan-card-features {
    .plan-card-features-feature {
      font-size: 15px;
      line-height: 22px;
      .plan-card-features-feature-icon {
        margin-top: 1px;
      }
      .plan-card-features-feature-bold {
        font-weight: 600;
      }
    }
  }

  .plan-card-go {
    text-align: center;
    .plan-card-go-button {
      width: 100%;
      height: 40px;
      .v-btn__content {
        font-weight: 400;
      }
      &.big-button {
        .v-btn__content {
          font-size: 16px;
          text-transform: none;
        }
      }
    }
    .plan-card-go-disallowed-plan {
      font-size: 11px;
      color: #636363;
    }
    .plan-card-go-close a {
      text-decoration: underline;
      font-size: 14px;
      color: #636363;
    }
  }
  .plan-card-disclaimer {
    color: rgba(0, 0, 0, 0.38);
    font-size: 12px;
    line-height: 20px;
    .plan-card-disclaimer-stripe {
      color: #8079fe;
    }
  }
}
</style>
